"use client"

import * as React from "react"

import type { AnalyticsEvent } from "@workspace/analytics"

import { useAnalytics } from "./analytics-provider"

/** Max remembered keys per storage scope — enough for any real session. */
const MAX_REMEMBERED = 50
const STORAGE_KEY = "itd:analytics:fired"

/**
 * Records `key` in session/local storage; false when it was already there.
 * Storage can throw (private mode, blocked site data) — then fall back to
 * "not seen", leaving the component's ref as the only guard.
 */
function claimOnce(scope: "session" | "local", key: string): boolean {
  try {
    const storage =
      scope === "session" ? window.sessionStorage : window.localStorage
    const raw = storage.getItem(STORAGE_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    const seen = Array.isArray(parsed) ? (parsed as unknown[]) : []
    if (seen.includes(key)) return false
    storage.setItem(
      STORAGE_KEY,
      JSON.stringify([...seen, key].slice(-MAX_REMEMBERED))
    )
    return true
  } catch {
    return true
  }
}

/**
 * Fires a server-built analytics event once when mounted. Lets Server
 * Components (item, checkout and confirmation pages) emit events without
 * becoming client components.
 *
 * `once.key` dedupes within this mount (StrictMode's double effect, RSC
 * refreshes that hand down a fresh `event` object). `once.scope` extends that
 * to the tab session or the browser — for pages that legitimately re-render
 * for the same event: begin_checkout on back-navigation, purchase on a
 * confirmation-page reload.
 */
export function TrackEvent({
  event,
  once,
}: {
  event: AnalyticsEvent | null
  once: { key: string; scope?: "mount" | "session" | "local" }
}) {
  const { tag, track } = useAnalytics()
  const firedKey = React.useRef<string | null>(null)
  const { key, scope = "mount" } = once

  React.useEffect(() => {
    if (!tag || !event || firedKey.current === key) return
    firedKey.current = key
    if (scope !== "mount" && !claimOnce(scope, key)) return
    track(event)
  }, [tag, event, key, scope, track])

  return null
}
